/* eslint-disable no-unused-vars */
/* eslint-disable indent */
import React, { useState, useEffect } from "react";
import { Card, Row, Col, message, Button, Input } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import moment from "moment";
import {
    DropDownComponent,
    TableComponent,
    CalendarComponent,
    RefreshTimestamp
} from "../../CommonComponents";
import "../styles.scss";
import Heading from "../../../../components/Heading";
import { getHubList, getHubCPList } from "../../../../services/api/hub";
import { ReportsService } from "../../../../services/api/reports";
import { downloadIncomingLCRReport } from "../../../../utils/downloadUtils";
import { useNavigate } from "react-router-dom";
import API from "../../../../api";
import urls from "../../../../api/urls";
import { showErrorMessage } from "../../../../utils";

const IncomingLCRReport = () => {
    const FILTER_STORAGE_KEY = "INCOMING_LCR_REPORT_FILTERS";

    const [loading, setLoading] = useState(false);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [dateRange, setDateRange] = useState([
        moment().subtract(1, "days"),
        moment()
    ]);
    const [hubList, setHubList] = useState([]);
    const [selectedHubs, setSelectedHubs] = useState([]);
    const [cpList, setCpList] = useState([]);
    const [selectedDestinationHub, setSelectedDestinationHub] = useState([]);
    const [statusList] = useState([
        { value: "LCR-INSCAN", label: "LCR Inscan" },
        { value: "LCR-OUTSCAN", label: "LCR Outscan" },
        { value: "COMPLETED", label: "Completed" },
        { value: "BOOKED", label: "Booked" },
        { value: "HUB-IN-SCANNED", label: "Hub In-Scanned" },
        { value: "HUB-OUTSCAN", label: "Hub Outscan" },
        // Add more LCR-specific statuses as needed
    ]);
    const [selectedStatus, setSelectedStatus] = useState([]);
    const [stopLcrNums, setStopLcrNums] = useState("");
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
    });
    const [reportData, setReportData] = useState([]);
    const [hubUser, setHubUser] = useState();
    const navigate = useNavigate();
    const [lastsynced, setLastsynced] = useState(null);
    const [nextScheduledRefresh, setNextScheduledRefresh] = useState(null);

    // Helper function to save filters to sessionStorage
    const saveFiltersToStorage = () => {
        const filters = {
            dateRange: dateRange ? [
                dateRange[0]?.format("YYYY-MM-DD"),
                dateRange[1]?.format("YYYY-MM-DD")
            ] : null,
            selectedHubs,
            selectedDestinationHub,
            selectedStatus,
            stopLcrNums,
            pagination: {
                current: pagination.current,
                pageSize: pagination.pageSize
            }
        };
        sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
    };

    // Helper function to restore filters from sessionStorage
    const restoreFiltersFromStorage = () => {
        try {
            const savedFilters = sessionStorage.getItem(FILTER_STORAGE_KEY);
            if (savedFilters) {
                const filters = JSON.parse(savedFilters);
                if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
                    setDateRange([
                        moment(filters.dateRange[0]),
                        moment(filters.dateRange[1])
                    ]);
                }
                if (filters.selectedHubs && filters.selectedHubs.length > 0) {
                    setSelectedHubs(filters.selectedHubs);
                }
                if (filters.selectedDestinationHub && filters.selectedDestinationHub.length > 0) {
                    setSelectedDestinationHub(filters.selectedDestinationHub);
                }
                if (filters.selectedStatus && filters.selectedStatus.length > 0) {
                    setSelectedStatus(filters.selectedStatus);
                }
                if (filters.stopLcrNums) {
                    setStopLcrNums(filters.stopLcrNums);
                }
                if (filters.pagination) {
                    setPagination(prev => ({
                        ...prev,
                        current: filters.pagination.current || prev.current,
                        pageSize: filters.pagination.pageSize || prev.pageSize
                    }));
                }
                // Clear the saved filters after restoring
                sessionStorage.removeItem(FILTER_STORAGE_KEY);
                return true;
            }
        } catch (error) {
            console.error("Error restoring filters:", error);
        }
        return false;
    };

    // Fetch hub list on mount
    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                const hubs = await getHubList();
                setHubList(hubs);
                // Restore filters after hub list is loaded
                restoreFiltersFromStorage();
            } catch {
                message.error("Failed to fetch hub list");
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line
    }, []);

    // Set default hub for HUBADMIN users (only if no hubs are already selected and no filters were restored)
    useEffect(() => {
        // Parse user and userId from localStorage with fallback to empty object
        const user = JSON.parse(localStorage.getItem("userinfo") || "{}{}");
        let userid = JSON.parse(localStorage.getItem("userId") || "{}{}");
        setHubUser(user?.roles?.[0]);
        const activeHub = JSON.parse(localStorage.getItem("active_hub"));
        const activeHubId = activeHub?.hubId;

        // Check if there are saved filters - if yes, don't set default hub (restore will handle it)
        const hasSavedFilters = sessionStorage.getItem(FILTER_STORAGE_KEY);
        // Only set default hub if no hubs are selected and no filters were saved (to avoid overriding restored filters)
        if (hubList.length > 0 && selectedHubs.length === 0 && !hasSavedFilters) {
            if (activeHubId) {
                const defaultHub = hubList.find((hub) => String(hub.value) === String(activeHubId));
                if (defaultHub) {
                    setSelectedHubs([defaultHub.value]);
                }
            } else if ((user?.roles?.[0] === "ROLE_HUBADMIN" || user?.roles?.[0] === "ROLE_HUBOPERATOR")) {
                const defaultHub = hubList.find(hub => String(hub.value) === String(userid?.userInfo?.premiseId));
                if (defaultHub) {
                    setSelectedHubs([defaultHub.value]);
                }
            }
        }
    }, [hubList]);

    // Fetch report data when filters or pagination change
    useEffect(() => {
        // Wait for hubUser to be initialized
        if (!hubUser) {
            return;
        }
        // Only restrict for HUBADMIN with no hub selected
        if ((hubUser === "ROLE_HUBADMIN" || hubUser === "ROLE_HUBOPERATOR") && selectedHubs.length === 0) {
            return;
        }
        // Guard against invalid dateRange
        if (!dateRange || !dateRange[0] || !dateRange[1]) {
            return;
        }
        fetchReport();
        // eslint-disable-next-line
    }, [hubUser, selectedHubs, selectedDestinationHub, selectedStatus, stopLcrNums, dateRange, pagination.current, pagination.pageSize]);

    const fetchReport = async () => {
        setLoading(true);
        try {
            setReportData([]);
            const selectedHubData = hubList.filter(hub => selectedHubs?.includes(hub.value));
            const selectedDestHubData = hubList.filter(hub => selectedDestinationHub?.includes(hub.value));
            // Guard against invalid dateRange
            const hasValidDates = Array.isArray(dateRange) && dateRange[0] && dateRange[1] && typeof dateRange[0].format === "function" && typeof dateRange[1].format === "function";
            if (!hasValidDates) {
                setLoading(false);
                return;
            }
            const params = {
                ...(selectedHubData?.length > 0 && { originHubId: selectedHubData.map(hub => hub.value).join(",") }),
                ...(selectedDestHubData?.length > 0 && { destinationHubId: selectedDestHubData.map(hub => hub.value).join(",") }),
                ...(selectedHubData?.length > 0 && { hubIds: selectedHubData.map(hub => hub.value) }),
                ...(hasValidDates && {
                    fromDate: dateRange[0].format("YYYY-MM-DD"),
                    toDate: dateRange[1].format("YYYY-MM-DD")
                }),
                ...(selectedStatus?.length > 0 && { currentStatus: selectedStatus.join(",") }),
                ...(stopLcrNums && { stopLcrNums: stopLcrNums }),
                page: (pagination.current - 1), // Convert to 0-based
                size: pagination.pageSize
            };

            console.log("Incoming LCR Report params:", params);

            // Call the Incoming LCR report API

            const { data, total, lastRefreshedAt, nextScheduledRefresh } = await ReportsService.getIncomingLCRReport(params);
            setLastsynced(lastRefreshedAt);
            setNextScheduledRefresh(nextScheduledRefresh);
            setReportData(data);
            setPagination(prev => ({
                ...prev,
                total,
            }));
        } catch {
            showErrorMessage("Failed to fetch report data");
            setReportData([]);
            setPagination(prev => ({
                ...prev,
                total: 0
            }));
        } finally {
            setLoading(false);
        }
    };

    const handleTableChange = (newPagination) => {
        setPagination(prev => ({
            ...prev,
            current: newPagination.current,
            pageSize: newPagination.pageSize
        }));
    };

    const handleDownload = async () => {
        const hasValidDates = Array.isArray(dateRange) && dateRange[0] && dateRange[1] && typeof dateRange[0].format === "function" && typeof dateRange[1].format === "function";
        if (!hasValidDates) {
            message.error("Please select a date range for download");
            return;
        }
        setDownloadLoading(true);
        try {
            const selectedHubData = hubList.filter(hub => selectedHubs?.includes(hub.value));
            const selectedDestHubData = hubList.filter(hub => selectedDestinationHub?.includes(hub.value));

            const downloadParams = {
                ...(selectedHubData?.length > 0 && { originHubId: selectedHubData.map(hub => hub.value).join(",") }),
                ...(selectedDestHubData?.length > 0 && { destinationHubId: selectedDestHubData.map(hub => hub.value).join(",") }),
                ...(selectedHubData?.length > 0 && { hubIds: selectedHubData.map(hub => hub.value) }),
                ...(hasValidDates && {
                    fromDate: dateRange[0].format("YYYY-MM-DD"),
                    toDate: dateRange[1].format("YYYY-MM-DD")
                }),
                ...(selectedStatus?.length > 0 && { currentStatus: selectedStatus.join(",") }),
                ...(stopLcrNums && { stopLcrNums: stopLcrNums })
            };

            console.log("incoming (Outgoing LCRfrom click):", selectedHubData.map(hub => hub.value).join(","));


            await downloadIncomingLCRReport(downloadParams);
            message.success("Report downloaded successfully");
        } catch (error) {
            console.error("Download error:", error);
            showErrorMessage("Failed to download report");
        } finally {
            setDownloadLoading(false);
        }
    };

    // Handler for cell clicks (fetch details and navigate)
    const handleCellClick = async (type, record) => {
        // Save current filters before navigating
        saveFiltersToStorage();

        let status = type;
        // Map shipment types to required API status
        if (type === "shipmentOut") status = "SHIPMENT_OUT";
        else if (type === "shipmentIn") status = "SHIPMENT_IN";
        else if (type === "shipmentPending") status = "SHIPMENT_PENDING";
        // Map in-bag shipment types to regular shipment statuses
        else if (type === "inBagShipmentOut") status = "SHIPMENT_OUT";
        else if (type === "inBagShipmentIn") status = "SHIPMENT_IN";
        else if (type === "inBagShipmentVariance") status = "SHIPMENT_PENDING";
        // Map bag types to required API status
        else if (type === "bagOut") status = "BAG_OUT";
        else if (type === "bagIn") status = "BAG_IN";
        else if (type === "bagPending") status = "BAG_PENDING";

        // Determine detail type and endpoint
        const isInBagShipment = ["inBagShipmentOut", "inBagShipmentIn", "inBagShipmentVariance"].includes(type);
        const isBag = ["bagOut", "bagIn", "bagPending"].includes(type);
        let detailType = isBag ? "Bag" : "Shipment";
        let endpoint = isBag ? urls.BAG_DETAIL_VIEW_LCR : (isInBagShipment ? urls.IN_BAG_SHIPMENT_DETAIL_VIEW_LCR : urls.SHIPMENT_DETAIL_VIEW_LCR);
        let lcrNum = record.stopLcrNum;
        let vehicleNum = record.vehicleNum || record.vehicle_num;
        let currentHub = record.fromHub || record.current_hub;
        try {
            const queryParams = {
                lcrNum,
                type: status,
            };
            const apiResult = await API.Api(endpoint).getBagDetailsParams(queryParams);
            if (apiResult.response.data.code == 200) {
                let dataArr = apiResult.response.data.data;
                console.log("datat derom..", JSON.stringify(dataArr));
                if (apiResult.response.data.data.length > 0) {
                    navigate("/detailed-inout-view", {
                        state: {
                            vehicle_lcr_id: lcrNum,
                            vehicle_num: vehicleNum,
                            current_hub: currentHub,
                            type: detailType,
                            status,
                            data: dataArr,
                            pageFrom: "InComingLCReport",
                            url: "/incoming-lcr"
                        },
                    });
                } else {
                    showErrorMessage(apiResult.response.data.message);
                }
            } else {
                showErrorMessage("Something went wrong");
            }

            //console.log("apiResult:", JSON.stringify(apiResult.response.data.message, null, 2));

        } catch (error) {
            message.error("An error occurred while fetching details");
        }
    };

    return (
        <div className="report-container">
            <div style={{ backgroundColor: "white", padding: "8px 16px", borderRadius: "8px" }}>
                <Heading title="Incoming LCR" navigateTo="/reports">
                    <div style={{ paddingTop: "5px" }}>
                        <RefreshTimestamp
                            lastRefreshedAt={lastsynced}
                            nextScheduledRefresh={nextScheduledRefresh}
                        />
                    </div>
                </Heading>
            </div>
            <Card className="report-card">
                <Row gutter={[16, 16]} className="filters-section">
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">From & To Date</div>
                        <CalendarComponent
                            className="custom-calendar"
                            type="range"
                            value={dateRange && dateRange[0] && dateRange[1] ? dateRange : [moment(), moment()]}
                            onChange={setDateRange}
                            format="YYYY-MM-DD"
                            allowClear
                        />
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">From Hub</div>
                        <DropDownComponent
                            className="small-dropdown"
                            placeholder="Destination Hub"
                            options={hubList}
                            onChange={setSelectedDestinationHub}
                            value={selectedDestinationHub}
                            allowClear
                            mode="multiple"
                            maxTagCount="responsive"
                            showSearch={true}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">To Hub</div>
                        <DropDownComponent
                            placeholder="Origin Hub"
                            options={hubList}
                            onChange={setSelectedHubs}
                            value={selectedHubs}
                            allowClear
                            mode={hubUser !== "ROLE_HUBADMIN" || hubUser !== "ROLE_HUBOPERATOR" ? "multiple" : "default"}
                            disabled={hubUser === "ROLE_HUBADMIN" || hubUser === "ROLE_HUBOPERATOR"}
                            maxTagCount="responsive"
                            showSearch={true}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Stop LCR Numbers</div>
                        <Input
                            placeholder="Enter comma separated LCR numbers"
                            value={stopLcrNums}
                            onChange={(e) => setStopLcrNums(e.target.value)}
                            allowClear
                        />
                    </Col>
                    <Col xs={24} sm={24} md={24} lg={24} style={{ display: "flex", alignItems: "flex-end", justifyContent: "flex-end" }}>
                        <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            loading={downloadLoading}
                            onClick={handleDownload}
                            disabled={!dateRange || !dateRange[0] || !dateRange[1]}
                        >
                            Download Report
                        </Button>
                    </Col>
                    {/* <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Status</div>
                        <DropDownComponent
                            placeholder="Select Status"
                            options={statusList}
                            onChange={setSelectedStatus}
                            value={selectedStatus}
                            allowClear
                            mode="multiple"
                            maxTagCount="responsive"
                        />
                    </Col>*/}
                </Row>
            </Card>
            {/* New Table for Provided Data */}
            <TableComponent
                columns={[
                    { title: "Date", dataIndex: "createdDate", key: "createdDate", sorter: true },
                    {
                        title: "Vehicle LCR Number", dataIndex: "vehicleLcrNum", key: "vehicleLcrNum", sorter: true, align: "center",
                        render: (text, record) => (
                            <span
                                style={{ color: "#2977ff", cursor: "pointer", textDecoration: "underline" }}
                                onClick={() => {
                                    const recordToStore = {
                                        ...record,
                                        vehicle_lcr_id: record.vehicleLcrNum
                                    };
                                    sessionStorage.setItem("LCR_REPORT_RECORD", JSON.stringify(recordToStore));
                                    window.open(window.location.origin + "/lcr-report", "_blank");
                                }}
                            >
                                {text}
                            </span>
                        )
                    },
                    {
                        title: "Stop LCR Number",
                        dataIndex: "stopLcrNum",
                        key: "stopLcrNum",
                        sorter: true,
                        render: (text, record) => (
                            <span
                                style={{ color: "#2977ff", cursor: "pointer", textDecoration: "underline" }}
                                onClick={() => {
                                    const recordToStore = {
                                        ...record,
                                        vehicle_lcr_id: record.stopLcrNum
                                    };
                                    sessionStorage.setItem("LCR_REPORT_RECORD", JSON.stringify(recordToStore));
                                    window.open(window.location.origin + "/lcr-report", "_blank");
                                }}
                            >
                                {text}
                            </span>
                        )
                    },
                    { title: "LCR Departed Date & Time", dataIndex: "tripDepartedTime", key: "tripDepartedTime", sorter: true, align: "center", },
                    { title: "From Hub", dataIndex: "fromHub", key: "fromHub", sorter: true },
                    { title: "Mode", dataIndex: "mode", key: "mode", sorter: true },
                    { title: "Vehicle Number", dataIndex: "vehicleNum", key: "vehicleNum", sorter: true, align: "center", },
                    { title: "Flight Number", dataIndex: "flightNum", key: "flightNum", sorter: true, align: "center", },
                    {
                        title: "Bags Out | In",
                        dataIndex: "bagOutCount",
                        key: "bagOutCount",
                        align: "center",
                        sorter: true,
                        width: 20,
                        render: (text, record) => (
                            <>
                                {record.bagOutCount == 0 ? <span>{record.bagOutCount ?? ""}</span> : (
                                    <span
                                        style={{ color: "#2977ff", cursor: "pointer", textDecoration: "underline" }}
                                        onClick={() => handleCellClick("bagOut", record)}
                                    >
                                        {record.bagOutCount ?? ""}
                                    </span>)}
                                <span style={{ fontWeight: "bold", padding: "0 4px" }}>|</span>
                                {record.bagInCount == 0 ? <span>{record.bagInCount ?? ""}</span> : (
                                    <span
                                        style={{ color: "#2977ff", cursor: "pointer", textDecoration: "underline" }}
                                        onClick={() => handleCellClick("bagIn", record)}
                                    >
                                        {record.bagInCount ?? ""}
                                    </span>)}
                            </>
                        )
                    },
                    {
                        title: "Bags Pending",
                        dataIndex: "bagPendingCount",
                        key: "bagPendingCount",
                        sorter: true,
                        align: "center",
                        width: 20,
                        render: (text, record) => (
                            <>
                                {record.bagPendingCount == 0 ? <span>{record.bagPendingCount ?? ""}</span> : (
                                    <span
                                        style={{ color: "#2977ff", cursor: "pointer", textDecoration: "underline" }}
                                        onClick={() => handleCellClick("bagPending", record)}
                                    >
                                        {record.bagPendingCount ?? ""}
                                    </span>)}
                            </>
                        )
                    },
                    {
                        title: "In Bag Shipments Out | In",
                        dataIndex: "inBagShipmentOutCount",
                        key: "inBagShipmentOutCount",
                        align: "center",
                        sorter: true,
                        width: 20,
                        render: (text, record) => (
                            <>
                                {record.inBagShipmentOutCount == 0 ? <span>{record.inBagShipmentOutCount ?? ""}</span> : (
                                    <span
                                        style={{ color: "#2977ff", cursor: "pointer", textDecoration: "underline" }}
                                        onClick={() => handleCellClick("inBagShipmentOut", record)}
                                    >
                                        {record.inBagShipmentOutCount ?? ""}
                                    </span>)}
                                <span style={{ fontWeight: "bold", padding: "0 4px" }}>|</span>
                                {record.inBagShipmentInCount == 0 ? <span>{record.inBagShipmentInCount ?? ""}</span> : (
                                    <span
                                        style={{ color: "#2977ff", cursor: "pointer", textDecoration: "underline" }}
                                        onClick={() => handleCellClick("inBagShipmentIn", record)}
                                    >
                                        {record.inBagShipmentInCount ?? ""}
                                    </span>)}
                            </>
                        )
                    },
                    {
                        title: "In Bag Shipments Pending",
                        dataIndex: "inBagShipmentVariance",
                        key: "inBagShipmentVariance",
                        sorter: true,
                        align: "center",
                        width: 20,
                        render: (text, record) => (
                            <>
                                {record.inBagShipmentVariance == 0 ? <span>{record.inBagShipmentVariance ?? ""}</span> : (
                                    <span
                                        style={{ color: "#2977ff", cursor: "pointer", textDecoration: "underline" }}
                                        onClick={() => handleCellClick("inBagShipmentVariance", record)}
                                    >
                                        {record.inBagShipmentVariance ?? ""}
                                    </span>)}
                            </>
                        )
                    },
                    {
                        title: "Individual Shipments Out | In",
                        dataIndex: "shipmentOutCount",
                        key: "shipmentOutCount",
                        align: "center",
                        sorter: true,
                        width: 20,
                        render: (text, record) => (
                            <>
                                {record.shipmentOutCount == 0 ? <span>{record.shipmentOutCount ?? ""}</span> : (
                                    <span
                                        style={{ color: "#2977ff", cursor: "pointer", textDecoration: "underline" }}
                                        onClick={() => handleCellClick("shipmentOut", record)}
                                    >
                                        {record.shipmentOutCount ?? ""}
                                    </span>)}
                                <span style={{ fontWeight: "bold", padding: "0 4px" }}>|</span>
                                {record.shipmentInCount == 0 ? <span>{record.shipmentInCount ?? ""}</span> : (
                                    <span
                                        style={{ color: "#2977ff", cursor: "pointer", textDecoration: "underline" }}
                                        onClick={() => handleCellClick("shipmentIn", record)}
                                    >
                                        {record.shipmentInCount ?? ""}
                                    </span>)}
                            </>
                        )
                    },
                    {
                        title: "Individual Shipments Pending",
                        dataIndex: "shipmentPendingCount",
                        key: "shipmentPendingCount",
                        sorter: true,
                        align: "center",
                        width: 20,
                        render: (text, record) => (
                            <>
                                {record.shipmentPendingCount == 0 ? <span>{record.shipmentPendingCount ?? ""}</span> : (
                                    <span
                                        style={{ color: "#2977ff", cursor: "pointer", textDecoration: "underline" }}
                                        onClick={() => handleCellClick("shipmentPending", record)}
                                    >
                                        {record.shipmentPendingCount ?? ""}
                                    </span>)}
                            </>
                        )
                    },
                    { title: "Co Loader", dataIndex: "coLoader", key: "coLoader", sorter: true, align: "center", },
                    { title: "Driver Name", dataIndex: "driverName", key: "driverName", sorter: true, align: "center", },
                    { title: "Driver Phone No", dataIndex: "driverPhoneNo", key: "driverPhoneNo", sorter: true, align: "center", },
                    { title: "Total Weight (kg)", dataIndex: "totalWeight", key: "totalWeight", sorter: true, align: "center", },
                    { title: "Status", dataIndex: "status", key: "status", sorter: true, align: "center", },
                    { title: "Is Anomaly", dataIndex: "isAnomaly", key: "isAnomaly", sorter: true, align: "center", },
                ]}
                dataSource={reportData}
                loading={loading}
                pagination={pagination}
                onChange={handleTableChange}
                rowKey="lcrNum"
                style={{ marginTop: 32 }}
            />
        </div>
    );
};

export default IncomingLCRReport;
